import styled from '@emotion/styled'
import { useState } from 'react'

import { Vacancy } from './Vacancy'
import { Color, theme } from '../../../constants'
import { Button, Div, Flex, Loader, Paper, Text } from '../../../elements'
import { useTranslations } from '../../../translate'
import { SectionHeading } from '../components/SectionHeading'
import { useCandidateView } from '../hooks/useCandidateView'
import { CandidateViewMatchingVacancy, CandidateViewVacancy } from '../types'

const ExpandButton = styled.div({
  borderBottomLeftRadius: theme.border.radius.normal,
  borderBottomRightRadius: theme.border.radius.normal,
  borderTopColor: Color.LIGHTER_BLUE,
  borderTopStyle: 'solid',
  borderTopWidth: theme.border.width.thin,
  color: Color.BLUE,
  fontSize: 14,
  fontWeight: theme.font.weight.bold,
  paddingBottom: 12,
  paddingLeft: 24,
  paddingRight: 24,
  paddingTop: 12,
  textAlign: 'center',
  width: '100%',
  '&:hover': {
    backgroundColor: Color.LIGHTER_BLUE,
    cursor: 'pointer',
  },
})

export type VacanciesProps = {
  compact?: boolean
  matchingVacancies: CandidateViewMatchingVacancy[]
  matchingVacanciesLoading: boolean
  onOpenVacanciesClick: () => void
  onVacancyClick: (vacancy: CandidateViewVacancy) => void
}

export const Vacancies = ({
  compact,
  matchingVacancies,
  matchingVacanciesLoading,
  onOpenVacanciesClick,
  onVacancyClick,
}: VacanciesProps) => {
  const { actor, isEditing, isMe } = useCandidateView()
  const t = useTranslations()

  const [isExpanded, setIsExpanded] = useState(false)

  if (isEditing() || !actor.can('read', 'vacancies')) {
    return null
  }

  if (matchingVacanciesLoading) {
    return (
      <Flex justify="center" styles={{ p: 4 }}>
        <Loader color={Color.DARK_MEDIUM_GREY} />
      </Flex>
    )
  }

  if (!matchingVacancies.length) {
    return null
  }

  const color = compact ? Color.DARK_BLUE : Color.DARK_GREY

  return (
    <Paper
      design={compact ? 'blue' : 'white'}
      styles={{
        alignItems: 'flex-start',
        flex: 'col',
      }}
    >
      <Div styles={{ mb: 2.5, p: 3.5, pb: 0 }}>
        <SectionHeading styles={{ color }}>
          {isMe
            ? t.candidate_view.vacancies.jobs_for_you()
            : t.candidate_view.vacancies.matching_vacancies()}
        </SectionHeading>
        <Text color={color} size={6}>
          {isMe
            ? t.candidate_view.vacancies.based_on_your_profile()
            : t.candidate_view.vacancies.based_on_the_candidates_profile()}
        </Text>
      </Div>
      <Flex col styles={{ w: 'full' }}>
        {matchingVacancies
          .slice(0, isExpanded ? 10 : 3)
          .sort((a, b) => b.score - a.score)
          .map((matchingVacancy) => (
            <Vacancy
              compact={compact}
              key={matchingVacancy.vacancy.id}
              matchingVacancy={matchingVacancy}
              onClick={onVacancyClick}
            />
          ))}
      </Flex>
      {isExpanded ? (
        <Div styles={{ p: 3.5, pt: 2.5 }}>
          <Button
            design="primary-inverted"
            onClick={onOpenVacanciesClick}
            size="sm"
          >
            {t.candidate_view.vacancies.see_n_matching_jobs()}
          </Button>
        </Div>
      ) : (
        <ExpandButton onClick={() => setIsExpanded(true)}>
          {t.candidate_view.vacancies.show_more()}
        </ExpandButton>
      )}
    </Paper>
  )
}
